<template>
  <el-dialog
    class="dialog-wrapper roles-modal"
    visible
    :show-close="false"
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="
        isModalEdit
          ? $t('customersLocationsManagement.update.updateCustomerLocation')
          : $t('customersLocationsManagement.create.createCustomerLocation')
      "
    />

    <div class="customer-data">
      <h2>{{ `${customer.name}` }}</h2>
    </div>

    <ValidationObserver
      ref="customerLocationForm"
      v-slot="{ handleSubmit, invalid }"
    >
      <el-form
        ref="form"
        label-position="top"
      >
        <el-row
          :gutter="20"
          type="flex"
        >
          <el-col :span="10">
            <FormItem
              rules="required"
              :label="$t('customersLocationsManagement.create.lab')"
            >
              <el-select
                v-model="formData.department"
                placeholder="Select"
                clearable
              >
                <el-option
                  v-for="laboratory in laboratoriesList"
                  :key="laboratory.value"
                  :label="laboratory.label"
                  :value="laboratory.value"
                />
              </el-select>
            </FormItem>
          </el-col>
          <el-col :span="10">
            <FormItem
              rules="required"
              :label="$t('customersLocationsManagement.create.location')"
            >
              <el-autocomplete
                v-model="locationName"
                style="width: 100%"
                :fetch-suggestions="locationsList"
                :trigger-on-focus="false"
                value-key="label"
                clearable
                placeholder=""
                @select="
                  (item) => {
                    formData.locationId = item.value;
                  }
                "
              >
                <template slot-scope="{ item }">
                  <AutocompleteItem :item="item" />
                </template>
              </el-autocomplete>
            </FormItem>
          </el-col>
        </el-row>

        <FormButtons>
          <el-button
            type="secondary"
            @click.prevent="abortAction"
          >
            {{ $t('customersLocationsManagement.create.abortAction') }}
          </el-button>
          <el-button
            type="primary"
            native-type="submit"
            :disabled="invalid"
            @click.prevent="handleSubmit(createUpdateCustomerLocation)"
          >
            {{
              isModalEdit
                ? $t(
                    'customersLocationsManagement.update.updateCustomerLocation'
                  )
                : $t(
                    'customersLocationsManagement.create.createCustomerLocation'
                  )
            }}
          </el-button>
        </FormButtons>
      </el-form>
    </ValidationObserver>
  </el-dialog>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { laboratories } from '@/config/constants';
  import { locationsManagementApi } from '@/api';

  import FormItem from '@/components/FormItem.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  import FormButtons from '@/components/FormButtons.vue';
  import AutocompleteItem from '@/components/AutocompleteItem.vue';

  const defaultData = {
    department: '',
    locationId: '',
    customerId: null,
  };

  export default {
    name: 'CreateEditCustomerLocation',
    components: {
      FormItem,
      ContentHeader,
      FormButtons,
      AutocompleteItem,
    },
    props: {
      isModalEdit: { type: Boolean, default: false },
      initialData: { type: Object, default: () => defaultData },
      customer: { type: Object, default: () => {} },
    },
    data() {
      return {
        formData: { ...defaultData },
        locationName: null,
      };
    },
    computed: {
      ...mapGetters(['forms']),
      laboratoriesList() {
        return Object.values(laboratories).map((val) => ({
          value: val,
          label: this.$t(`enums.laboratories.${val}`),
        }));
      },
    },
    async created() {
      this.formData.customerId = this.customer.id;
      try {
        if (this.isModalEdit) {
          const { department, locationId, customerId } = this.initialData;
          this.formData = {
            ...defaultData,
            department,
            locationId,
            customerId,
          };
          if (locationId) {
            const response = await locationsManagementApi.getLocation(
              locationId
            );
            this.locationName = response.data.name;
          }
        }
      } catch (error) {
        this.$notify({
          showClose: true,
          type: 'error',
          message: error.response.data.error.message,
        });
      }
    },
    methods: {
      async createUpdateCustomerLocation() {
        this.formData.customerId = Number(this.customer.id);
        try {
          if (this.isModalEdit) {
            await locationsManagementApi.customerLocationsPatch(
              this.initialData.id,
              this.formData
            );
          } else {
            await locationsManagementApi.customerLocationsCreate(this.formData);
          }
          this.$notify({
            showClose: true,
            type: 'success',
            message: this.$t('customersLocationsManagement.update.success'),
          });
          this.formData = { ...defaultData };
          this.$refs.customerLocationForm.reset();
          this.$emit('dialogClose', { success: true });
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      abortAction() {
        this.formData = { ...defaultData };
        this.$refs.customerLocationForm.reset();
        this.$emit('dialogClose', { success: false });
      },
      async locationsList(value, cb) {
        if (value && value.length >= 3) {
          const response = await locationsManagementApi.autocompleteFetch(
            'name',
            value,
            false
          );
          const locations = response.data.map((c) => ({
            label: c.name,
            value: c.id,
          }));
          cb(locations);
        } else {
          cb([]);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .customer-data {
    color: $--color-white;
    margin-bottom: 1.5rem;
    font-size: 18px;
  }
</style>
